import React from 'react';
import { useHistory } from 'react-router-dom';
import Bugsnag, { BreadcrumbType, NotifiableError } from '@bugsnag/js';
import { parse as parseQuery, stringify as querify } from 'qs';
import TagManager from 'react-gtm-module';

import { getClientToken } from '../api/oauth';
import { dispatchUserActivity } from '../utils/eventDispatchers';
import appConfig from '../appConfig';
import GeocomplyContext from '../contexts/GeocomplyContext/GeocomplyContext';

const usePortalGlobal = (user?: PortalUser | null) => {
	const history = useHistory();
	const { geolocationToken, showModal, requestPending, geolocationError, sdkState, requestGeolocation } =
		React.useContext(GeocomplyContext);

	window.portal = {
		user: {
			username: () => user?.name,
			status: () => user?.status?.name,
			account_balance: () => user?.balance,
			isLoggedIn: () => !!user?.id,
			limits: () => user?.limits,
			pin: () => user?.pin,
		},
		GTM: {
			logDatalayer: (dataLayer: { event: string; [key: string]: any }) => {
				if (!appConfig.googleTagManagerID) return;

				TagManager.dataLayer({
					dataLayer,
				});
			},
			logUserDetails: () => {
				// we cannot use the generic method above because the client app's user has no knowledge of the user's pin
				if (!user || !appConfig.googleTagManagerID) return;

				TagManager.dataLayer({
					dataLayer: {
						event: 'user_details',
						user_id: user?.pin,
						first_name: user?.first_name,
						last_name: user?.last_name,
						user_name: user?.name,
						email: user?.email,
						loggedIn: !!user?.id,
					},
				});
			},
		},
		client: {
			/**
			 * @msg: {type, title, message, timeout}
			 */
			notify: (msg: string) => {
				console.log('NOTIFY', msg);
			},
			getAuthToken: (id: number) => getClientToken(id),
			showLogin: () => {
				const search = parseQuery(window.location.search || '', {
					ignoreQueryPrefix: true,
				});

				history.replace({
					pathname: window.location.pathname,
					search: querify({ ...search, modal: 'login' }, { addQueryPrefix: true }),
				});
			},
			showDeposit: (defaultAmount: number, warningMessage: string) => {
				const search = parseQuery(window.location.search || '', {
					ignoreQueryPrefix: true,
				});

				history.replace({
					pathname: window.location.pathname,
					search: querify({ ...search, modal: 'deposit' }, { addQueryPrefix: true }),
					state: { defaultAmount, warningMessage },
				});
			},
			activityHearthbeat: () => {
				dispatchUserActivity();
			},
		},
		geocomply: {
			getStatus: () => ({
				hasModalError: !!geolocationError || sdkState.localClientState === 'not-installed',
				pending: requestPending,
				token: geolocationToken?.token ?? null,
			}),
			requestGeolocation,
			showModal,
		},
		bugsnag: {
			leaveBreadcrumb: (message: string, metaData?: { [key: string]: any }, type?: BreadcrumbType) => {
				Bugsnag.leaveBreadcrumb(message, metaData, type);
			},
			notify: (error: NotifiableError) => {
				Bugsnag.notify(error);
			},
		},
	};
};

export default usePortalGlobal;
